import {scrubContactData} from '../summary/scrubContactData';

export type IcsStart =
    | {kind: 'datetime'; utc: Date}
    | {kind: 'date'; date: string};

export interface IcsEvent {
    uid: string;
    title: string;
    start: IcsStart;
    durationMinutes: number;
    location?: string | null;
    description?: string | null;
    stamp: Date;
}

const CRLF = '\r\n';
const MAX_OCTETS = 75;
const encoder = new TextEncoder();

const pad = (value: number): string => String(value).padStart(2, '0');

export const formatUtc = (date: Date): string =>
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;

export const compactDate = (isoDate: string): string => isoDate.replace(/-/g, '');

export const nextDay = (isoDate: string): string => {
    const [year, month, day] = isoDate.split('-').map(Number);
    const next = new Date(Date.UTC(year, month - 1, day + 1));
    return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
};

export const escapeText = (value: string): string =>
    value
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r\n|\r|\n/g, '\\n');

export const foldLine = (line: string): string => {
    const chunks: string[] = [];
    let current = '';
    let octets = 0;
    for (const char of line) {
        const size = encoder.encode(char).length;
        const limit = chunks.length === 0 ? MAX_OCTETS : MAX_OCTETS - 1;
        if (octets + size > limit) {
            chunks.push(current);
            current = '';
            octets = 0;
        }
        current += char;
        octets += size;
    }
    chunks.push(current);
    return chunks.join(`${CRLF} `);
};

export const buildIcs = (event: IcsEvent): string => {
    const timing = event.start.kind === 'datetime'
        ? [
            `DTSTART:${formatUtc(event.start.utc)}`,
            `DTEND:${formatUtc(new Date(event.start.utc.getTime() + event.durationMinutes * 60_000))}`,
        ]
        : [
            `DTSTART;VALUE=DATE:${compactDate(event.start.date)}`,
            `DTEND;VALUE=DATE:${compactDate(nextDay(event.start.date))}`,
        ];
    const location = event.location ? scrubContactData(event.location) : '';
    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//PhoneHQ//Call insights//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `UID:${event.uid}`,
        `DTSTAMP:${formatUtc(event.stamp)}`,
        ...timing,
        `SUMMARY:${escapeText(event.title)}`,
        ...(location ? [`LOCATION:${escapeText(location)}`] : []),
        ...(event.description ? [`DESCRIPTION:${escapeText(event.description)}`] : []),
        'END:VEVENT',
        'END:VCALENDAR',
    ];
    return lines.map(foldLine).join(CRLF) + CRLF;
};

export const meetingUid = (callId: number, index: number): string => `call-${callId}-${index + 1}@phonehq`;
